import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileDown, Check } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { LeadMagnetModal } from "../LeadMagnetModal";
import { trackEvent } from "../../lib/analytics";

export const LeadMagnet = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    trackEvent("lead_esg_report_opened", {
      event_category: "engagement",
      event_label: "ESG Report CTA clicked",
    });
    setOpen(true);
  };

  const bullets = [t.lead.bullet_1, t.lead.bullet_2, t.lead.bullet_3];

  return (
    <section
      id="lead-magnet"
      data-testid="lead-magnet-section"
      className="relative bg-[#0D2F4C] text-white overflow-hidden"
    >
      {/* Gold accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A961]/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left: content */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">
              {t.lead.overline}
            </div>
            <h2
              className="text-4xl sm:text-5xl leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.lead.title}
            </h2>
            <p className="text-base text-white/75 leading-relaxed max-w-xl mb-8">
              {t.lead.subtitle}
            </p>

            <ul className="space-y-3 mb-10">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-white/85"
                >
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#C9A961]/15 flex items-center justify-center shrink-0">
                    <Check size={12} strokeWidth={3} className="text-[#C9A961]" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <button
              data-testid="lead-magnet-cta-btn"
              onClick={handleOpen}
              className="inline-flex items-center gap-3 bg-[#C9A961] text-[#0D2F4C] px-7 py-3.5 text-sm tracking-wide hover:bg-[#D4B775] transition-colors font-medium"
              style={{ borderRadius: 0 }}
            >
              <FileDown size={16} strokeWidth={2.2} />
              <span>{t.lead.cta}</span>
            </button>
          </motion.div>
        </div>

        {/* Right: visual */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative aspect-[3/4] max-w-sm mx-auto"
          >
            {/* Document mockup */}
            <div
              className="absolute inset-0 bg-gradient-to-br from-[#1B4870] to-[#0A243A] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] border border-[#C9A961]/30"
              style={{ borderRadius: 0 }}
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#C9A961]" />
              <div className="p-8 sm:p-10 h-full flex flex-col justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-3">
                    Richild Invest GBC
                  </div>
                  <div
                    className="text-3xl sm:text-4xl text-white leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Africa
                    <br />
                    <span className="text-[#C9A961]">ESG</span> Report
                  </div>
                  <div className="text-sm text-white/60 mt-3 tracking-wider">
                    2026 EDITION
                  </div>
                </div>

                <div>
                  <div className="h-px bg-[#C9A961]/40 mb-4" />
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                    Confidential · Insights
                  </div>
                </div>
              </div>
            </div>
            {/* Subtle stacked shadow */}
            <div
              className="absolute -bottom-3 -right-3 inset-x-0 inset-y-0 bg-[#C9A961]/15 -z-10"
              style={{ borderRadius: 0 }}
            />
          </motion.div>
        </div>
      </div>

      <LeadMagnetModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
};
